import React, { useEffect } from 'react';

import useFavoritedContent from '@smart-tv/hooks/use-favorited-content';

import { ContentItem } from '@smart-tv/typings/content';

import removeWatchedContentItemKMM from '@smart-tv/utils/kmm/removeContinueWatchingItem';

import actionableContentContextReducer from './actionable-content-context-reducer';

export interface ActionableContentContextProps {
  children: React.ReactNode | React.ReactNode[];
  items?: ContentItem[];
}

export interface ActionableContentContextValues {
  actionableItems?: ContentItem[];
  removeContent: (contentItem: ContentItem) => Promise<boolean>;
}

export const ActionableContentContext = React.createContext<ActionableContentContextValues>(
  {} as ActionableContentContextValues
);

export const ActionableContentConsumer = ActionableContentContext.Consumer;

export const ActionableContentProvider = (props: ActionableContentContextProps): JSX.Element => {
  const { children, items } = props;

  const [actionableItems, dispatch] = React.useReducer(actionableContentContextReducer, items);

  const { favoritedContent, unfavoritedContent } = useFavoritedContent();

  const removeContent = React.useCallback(async (contentItem: ContentItem) => {
    const { contentId } = contentItem;

    const response = await removeWatchedContentItemKMM(contentId as number);
    if (!response) return false;

    dispatch({ type: 'REMOVE_CONTENT', payload: contentId as number });

    return response;
  }, []);

  useEffect(() => {
    if (items) {
      dispatch({ type: 'SET_CONTENT', payload: items });
    }
  }, [items]);

  useEffect(() => {
    if (!favoritedContent && !unfavoritedContent) return;

    const contentId = favoritedContent?.contentId || unfavoritedContent?.contentId;

    if (!contentId) return;

    dispatch({ type: 'TOGGLE_FAVORITED_CONTENT', payload: contentId });
  }, [favoritedContent, unfavoritedContent]);

  return (
    <ActionableContentContext.Provider value={{ actionableItems, removeContent }}>
      {children}
    </ActionableContentContext.Provider>
  );
};

export default ActionableContentContext;
